import { axios } from "../config";
import { EMITTER_ERROR } from "../utils/constants";
import { authEndpoints } from "../utils/constants/httpConstants";
import EventEmitter from "../utils/emitter/EventEmitter";
import {
  INITIAL_ROUTE,
  INVALID_TENANT_ROUTE_PATH,
  LOGIN_PATH,
} from "../utils/constants/routes";
import { getTenantDomain, getTenantId } from "../utils/helpers/tenantConfig";
import { handleCreditResponse } from "../utils/creditMonitor/creditInterceptor";

const AUTH_ERROR_CODES = [401, 477];
const TENANT_INACTIVE_CODE = [419];

// Variables to handle multiple concurrent 401 requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add axios interceptor to include tenant headers in all requests
if (axios && axios.interceptors) {
  axios.interceptors.request.use(
    (config) => {
      const tenantDomain = getTenantDomain();
      const tenantId = getTenantId();

      if (tenantId) {
        config.headers["tenantId"] = tenantId;
      }

      if (tenantDomain) {
        config.headers["tenantDomain"] = tenantDomain;
        // The API's origin validation compares the request origin against the
        // tenant URL. On tenant subdomains they match naturally; on the shared
        // staging origin they don't, so assert it via the allowlisted header.
        config.headers["origin_host"] = `${tenantDomain}.reachiq.ai`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Global Response Interceptor for Session Management and Credit Monitoring
  axios.interceptors.response.use(
    (response) => {
      // Process successful responses for credit info
      try {
        handleCreditResponse(response?.data, response?.config);
      } catch (e) {
        // Silently fail
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // 1. Handle Authentication Failures (401/477)
      if (
        error.response &&
        AUTH_ERROR_CODES.includes(error.response.status) &&
        !originalRequest._retry &&
        // originalRequest.url !== authEndpoints.checkLogin
        !originalRequest.url.includes(authEndpoints.checkLogin)
      ) {
        if (isRefreshing) {
          // Add to queue if refresh is already in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          axios
            .post(authEndpoints.checkLogin, {})
            .then((res) => {
              processQueue(null);
              resolve(axios(originalRequest));
            })
            .catch((err) => {
              processQueue(err, null);
              // If refresh fails, redirect to login
              EventEmitter.emit(
                EMITTER_ERROR,
                "Session expired. Please login again.",
              );
              // We use window.location as we don't have navigate here
              if (window.location.pathname !== LOGIN_PATH) {
                window.location.href = LOGIN_PATH;
              }
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      // 2. Handle Inactive Tenant (419)
      if (
        error.response &&
        TENANT_INACTIVE_CODE.includes(error.response.status)
      ) {
        // Stash the BE reason/message so the invalid-tenant screen can show the right copy: a
        // reaper-PAUSED free tenant ({reason:"paused_inactive"}) -> "workspace paused, upgrade to
        // restore" vs a genuinely pending/under-review tenant -> the default "under process".
        try {
          sessionStorage.setItem(
            "tenant_block",
            JSON.stringify({
              reason: error.response.data?.data?.reason || "",
              message: error.response.data?.message || "",
            }),
          );
        } catch (_) {}
        if (window.location.pathname !== INVALID_TENANT_ROUTE_PATH) {
          window.location.href = INVALID_TENANT_ROUTE_PATH;
        }
      }

      // 3. Handle specific route redirects (426/427)
      if (
        error.response &&
        (error.response.status === 426 || error.response.status === 427)
      ) {
        if (window.location.pathname !== INITIAL_ROUTE) {
          window.location.href = INITIAL_ROUTE;
        }
      }

      // 4. Process error responses for credit status codes
      try {
        if (error?.response?.data) {
          const handled = handleCreditResponse(
            error.response.data,
            error?.config,
          );
          if (handled) {
            error.isCreditError = true;
            // Also set on response so callers using `throw error?.response` can detect it
            if (error.response) {
              error.response.isCreditError = true;
            }
          }
        }
      } catch (e) {
        // Silently fail
      }

      return Promise.reject(error);
    },
  );
}

// Helper function to create headers with tenant information (kept for backward compatibility if needed)
const createHeaders = (additionalHeaders = {}) => {
  const tenantDomain = getTenantDomain();
  const tenantId = getTenantId();

  const headers = { ...additionalHeaders };
  if (tenantId) headers["tenantId"] = tenantId;
  if (tenantDomain) headers["tenantDomain"] = tenantDomain;
  return headers;
};

// Simplified wrappers that now leverage the global interceptor
export const GET = async (url) => {
  return await axios.get(url);
};

export const POST = async (url, payload, navigate = () => {}) => {
  return await axios.post(url, payload);
};

export const getRequest = async (navigate, url) => {
  try {
    return await axios.get(url);
  } catch (error) {
    throw error?.response;
  }
};

export const postRequest = async (
  navigate,
  url,
  payload,
  headers = {},
  config = {},
) => {
  try {
    return await axios.post(url, payload, { headers, ...config });
  } catch (error) {
    throw error?.response;
  }
};

export const putRequest = async (navigate, url, payload) => {
  try {
    return await axios.put(url, payload);
  } catch (error) {
    throw error?.response;
  }
};

export const patchRequest = async (navigate, url, payload) => {
  try {
    return await axios.patch(url, payload);
  } catch (error) {
    throw error?.response;
  }
};

export const deleteRequest = async (url, payload) => {
  try {
    return await axios.delete(url, { data: payload });
  } catch (error) {
    throw error?.response;
  }
};

export const postStreamRequest = async (
  navigate,
  url,
  payload,
  headers = {},
) => {
  // Stream requests use 'fetch', so they need manual 401 handling or a retry wrapper
  try {
    const baseURL = axios.defaults.baseURL || "";
    let fullURL = url.startsWith("http")
      ? url
      : `${baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL}/${url.startsWith("/") ? url.slice(1) : url}`;

    const finalHeaders = createHeaders({
      ...headers,
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    });

    let response = await fetch(fullURL, {
      method: "POST",
      headers: finalHeaders,
      body: JSON.stringify(payload),
      credentials: "include",
    });

    if (!response.ok && AUTH_ERROR_CODES.includes(response.status)) {
      // Manual refresh for fetch-based stream
      await axios.post(authEndpoints.checkLogin, {});
      return await fetch(fullURL, {
        method: "POST",
        headers: finalHeaders,
        body: JSON.stringify(payload),
        credentials: "include",
      });
    }

    return response;
  } catch (error) {
    console.error("Stream request failed:", error);
    throw error;
  }
};
